import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "alert-reason-list-wrap" }
const _hoisted_2 = {
  key: 0,
  class: "alert-reason-list__metrics"
}
const _hoisted_3 = {
  key: 0,
  class: "alert-reason-list__metric-prefix"
}
const _hoisted_4 = {
  key: 1,
  class: "alert-reason-list__metric-label"
}
const _hoisted_5 = { class: "alert-reason-list__metric-value" }
const _hoisted_6 = { class: "alert-reason-list__metrics" }
const _hoisted_7 = {
  key: 0,
  class: "alert-reason-list__metric-prefix"
}
const _hoisted_8 = {
  key: 1,
  class: "alert-reason-list__metric-label"
}
const _hoisted_9 = { class: "alert-reason-list__metric-value" }

import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import DashboardAlertBadge from '@/inventoryDashboardModule/components/DashboardAlertBadge.vue';
import { DashboardAlert } from '@/inventoryDashboardModule/domains/entities/DashboardAlert';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';

type AlertReasonMetric = {
  label?: string;
  value: string;
  isSubtle?: boolean;
  prefix?: string;
};

/**
 * 数値を表示用に変換する
 * @param value - 変換する数値
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardAlertReasonList',
  props: {
    alerts: {},
    severity: {}
  },
  setup(__props: any) {

const t = i18n.t;

const props = __props;

const containerRef = ref<HTMLElement | null>(null);
const measureRef = ref<HTMLElement | null>(null);
const isReasonShown = ref(true);

let resizeObserver: ResizeObserver | undefined;

/**
 * アラート根拠の表示メトリクス
 */
const convertNumberToDisplayValue = (value: number | undefined) => value?.addComma() ?? '0';

/**
 * アラート根拠を横並び表示用のメトリクスに変換する
 * @param alert - 変換するアラート情報
 */
const convertAlertReasonToMetrics = (alert: DashboardAlert): AlertReasonMetric[] => {
  const reason = alert.reason;
  if (!reason) return [];

  const targetYearMonth = `(${DateService.convertIYearMonthToYYYYMMWithSlash(reason.targetYearMonth)})`;

  if ('latestCustomerForecastAmount' in reason) {
    return [
      {
        label: t('内示'),
        value: `${convertNumberToDisplayValue(reason.previousCustomerForecastAmount)} → ${convertNumberToDisplayValue(
          reason.latestCustomerForecastAmount
        )}`,
      },
      { value: targetYearMonth, isSubtle: true },
    ];
  }

  if ('salesResultAmount' in reason) {
    return [
      { label: t('内示'), value: convertNumberToDisplayValue(reason.customerForecastAmount) },
      { label: t('販売実績'), value: convertNumberToDisplayValue(reason.salesResultAmount), prefix: '→' },
      { value: targetYearMonth, isSubtle: true },
    ];
  }

  return [];
};

/**
 * アラート根拠メトリクスをテキストに変換する
 * @param metric - 変換するアラート根拠メトリクス
 */
const convertMetricToText = (metric: AlertReasonMetric) => {
  const body = metric.label ? `${metric.label} ${metric.value}` : metric.value;
  return metric.prefix ? `${metric.prefix} ${body}` : body;
};

/**
 * アラート根拠のtooltipテキストを生成する
 * @param alert - tooltipを表示するアラート情報
 */
const createTooltipText = (alert: DashboardAlert): string | undefined => {
  if (isReasonShown.value || !alert.reason) return undefined;

  return convertAlertReasonToMetrics(alert).map(convertMetricToText).join(' ');
};

/**
 * 表示状態を更新する
 */
const updateReasonVisibility = () => {
  const container = containerRef.value;
  const measure = measureRef.value;
  if (!container || !measure) return;

  isReasonShown.value = measure.scrollWidth <= container.clientWidth;
};

onMounted(async () => {
  await nextTick();
  updateReasonVisibility();

  resizeObserver = new ResizeObserver(updateReasonVisibility);
  if (containerRef.value) resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

watch(
  () => props.alerts,
  async () => {
    await nextTick();
    updateReasonVisibility();
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "containerRef",
      ref: containerRef,
      class: "alert-reason-list"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.alerts, (alert) => {
        return _withDirectives((_openBlock(), _createElementBlock("div", {
          key: alert.kind,
          class: "alert-reason-list__item"
        }, [
          _createVNode(DashboardAlertBadge, {
            alert: alert.kind,
            severity: _ctx.severity
          }, null, 8, ["alert", "severity"]),
          (isReasonShown.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(convertAlertReasonToMetrics(alert), (metric) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: `${alert.kind}-${metric.label ?? metric.value}`,
                    class: _normalizeClass(["alert-reason-list__metric", { 'alert-reason-list__metric--subtle': metric.isSubtle }])
                  }, [
                    (metric.prefix)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(metric.prefix), 1))
                      : _createCommentVNode("", true),
                    (metric.label)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(metric.label), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", _hoisted_5, _toDisplayString(metric.value), 1)
                  ], 2))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ])), [
          [
            _directive_tooltip,
            createTooltipText(alert),
            void 0,
            { top: true }
          ]
        ])
      }), 128))
    ], 512),
    _createElementVNode("div", {
      ref_key: "measureRef",
      ref: measureRef,
      class: "alert-reason-list alert-reason-list--measure",
      "aria-hidden": "true"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.alerts, (alert) => {
        return (_openBlock(), _createElementBlock("div", {
          key: alert.kind,
          class: "alert-reason-list__item"
        }, [
          _createVNode(DashboardAlertBadge, {
            alert: alert.kind,
            severity: _ctx.severity
          }, null, 8, ["alert", "severity"]),
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(convertAlertReasonToMetrics(alert), (metric) => {
              return (_openBlock(), _createElementBlock("span", {
                key: `${alert.kind}-${metric.label ?? metric.value}`,
                class: _normalizeClass(["alert-reason-list__metric", { 'alert-reason-list__metric--subtle': metric.isSubtle }])
              }, [
                (metric.prefix)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(metric.prefix), 1))
                  : _createCommentVNode("", true),
                (metric.label)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(metric.label), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("span", _hoisted_9, _toDisplayString(metric.value), 1)
              ], 2))
            }), 128))
          ])
        ]))
      }), 128))
    ], 512)
  ]))
}
}

})